// Shared ONNX Runtime session-creation helpers.
//
// Factored from the EP-routing + retry pattern in src/hair/OnnxRunner.js so
// new ONNX consumers (e.g. the TexAvatars pipeline under src/ogs/)
// reuse one implementation instead of duplicating the boilerplate.
//
// The `ort` module is passed in by the caller rather than imported here, so
// each consumer keeps its own bundle entrypoint. Both hair and TexAvatars import
// the plain 'onnxruntime-web' — in 1.26 that's the JSEP build, whose 'webgpu' EP
// is the JS-encoded JSEP backend (see src/ogs/inference/ort.ts); it is NOT
// WASM-only. 'onnxruntime-web/webgpu' selects the native C++/Dawn WebGPU EP
// instead, which this repo deliberately does not ship. Both serve their
// .wasm/.mjs from the same /onnxruntime/ URL (vite/vite_plugin_onnxruntime_static.mjs),
// so callers just point `ort.env.wasm.wasmPaths` there.

/** True if a WebGPU adapter can be acquired. Never throws. */
export async function hasWebGpuAdapter() {
  try {
    return !!(await navigator.gpu?.requestAdapter?.({ powerPreference: 'high-performance' }));
  } catch {
    return false;
  }
}

/**
 * Resolve an ordered executionProviders list from a `?ep=` override.
 *   ?ep=wasm   → ['wasm']
 *   ?ep=webgpu → ['webgpu','wasm']
 *   (none)     → preferWebgpu && adapter present ? ['webgpu','wasm'] : ['wasm']
 */
export async function resolveProviders(epOverride, { preferWebgpu = false } = {}) {
  if (epOverride === 'wasm') return ['wasm'];
  if (epOverride === 'webgpu') return ['webgpu', 'wasm'];
  if (preferWebgpu && (await hasWebGpuAdapter())) return ['webgpu', 'wasm'];
  return ['wasm'];
}

/**
 * Create an InferenceSession with `providers`, retrying once on plain WASM if
 * a non-WASM primary fails to compile/initialize. `extra` is spread into the
 * session options (e.g. preferredOutputLocation, enableProfiling) and is
 * preserved on the retry.
 */
export async function createSession(ort, url, providers, extra = {}) {
  try {
    return await ort.InferenceSession.create(url, {
      executionProviders: providers,
      graphOptimizationLevel: 'all',
      ...extra,
    });
  } catch (e) {
    if (providers[0] !== 'wasm') {
      console.warn(`[ort] session create failed for ${url}, retrying on wasm:`, e);
      return await ort.InferenceSession.create(url, {
        executionProviders: ['wasm'],
        graphOptimizationLevel: 'all',
        ...extra,
      });
    }
    throw e;
  }
}
